import React, { useEffect, useMemo, useState } from 'react';
import { calendarAPI } from './api';

const EVENT_TYPES = [
  ['court_date', 'Court date'],
  ['user_confirmed_deadline', 'User-confirmed deadline'],
  ['meeting', 'Meeting'],
  ['filing', 'Filing'],
  ['reminder', 'Reminder'],
  ['other', 'Other']
];

const REMINDER_OPTIONS = [
  ['', 'No reminder'],
  ['at_event_time', 'At event time'],
  ['one_hour', '1 hour before'],
  ['one_day', '1 day before'],
  ['three_days', '3 days before'],
  ['seven_days', '7 days before']
];

const TIMEZONES = ['America/Toronto', 'America/Thunder_Bay', 'UTC'];

const initialForm = () => ({
  title: '',
  description: '',
  event_type: 'court_date',
  all_day: false,
  start: '',
  end: '',
  timezone: 'America/Toronto',
  location: '',
  reminder_lead_time: ''
});

const wallParts = (instant, timeZone) => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  });
  return Object.fromEntries(
    formatter.formatToParts(instant)
      .filter(part => part.type !== 'literal')
      .map(part => [part.type, part.value])
  );
};

export const localDateTimeToIso = (value, timeZone) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) throw new Error('Enter a valid local date and time.');
  const desired = Date.UTC(
    Number(match[1]), Number(match[2]) - 1, Number(match[3]),
    Number(match[4]), Number(match[5]), 0
  );
  let candidate = desired;
  for (let index = 0; index < 3; index += 1) {
    const parts = wallParts(new Date(candidate), timeZone);
    const represented = Date.UTC(
      Number(parts.year), Number(parts.month) - 1, Number(parts.day),
      Number(parts.hour), Number(parts.minute), Number(parts.second)
    );
    candidate += desired - represented;
  }
  const confirmed = wallParts(new Date(candidate), timeZone);
  const expected = `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}`;
  const actual = `${confirmed.year}-${confirmed.month}-${confirmed.day}T${confirmed.hour}:${confirmed.minute}`;
  if (actual !== expected) {
    throw new Error('That local time does not exist because of a daylight-saving change.');
  }
  return new Date(candidate).toISOString();
};

const instantToLocalInput = (value, timeZone) => {
  const parts = wallParts(new Date(value), timeZone);
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

const instantToDateInput = (value, timeZone) => {
  const parts = wallParts(new Date(value), timeZone);
  return `${parts.year}-${parts.month}-${parts.day}`;
};

const readableType = (type) => (
  EVENT_TYPES.find(([value]) => value === type)?.[1] || 'Other'
);

const readableError = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return 'The calendar could not be updated. Please try again.';
};

const formatEventDate = (event) => {
  const options = event.all_day
    ? { dateStyle: 'medium', timeZone: event.timezone }
    : { dateStyle: 'medium', timeStyle: 'short', timeZone: event.timezone };
  return new Intl.DateTimeFormat('en-CA', options).format(new Date(event.start_at));
};

const reminderDescription = (reminder) => {
  if (!reminder) return null;
  const delivery = reminder.delivery_mode === 'simulated_local'
    ? 'Local simulation — no real email sent'
    : 'Real email delivery unavailable';
  return `${delivery}. Status: ${reminder.status}.`;
};

const Calendar = ({ user, setCurrentView }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [formError, setFormError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initialForm);

  const sortedEvents = useMemo(
    () => [...events].sort((left, right) => new Date(left.start_at) - new Date(right.start_at)),
    [events]
  );

  const loadEvents = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await calendarAPI.getEvents({ limit: 100 });
      setEvents(response.data.events || []);
    } catch (error) {
      setLoadError(readableError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) loadEvents();
  }, [user]);

  const updateForm = (name, value) => setForm(current => ({ ...current, [name]: value }));

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormError('');
    setForm(initialForm());
  };

  const beginAdd = () => {
    setEditingId(null);
    setForm(initialForm());
    setFormError('');
    setShowForm(true);
  };

  const beginEdit = (event) => {
    setEditingId(event.event_id);
    setForm({
      title: event.title,
      description: event.description || '',
      event_type: event.event_type,
      all_day: event.all_day,
      start: event.all_day
        ? instantToDateInput(event.start_at, event.timezone)
        : instantToLocalInput(event.start_at, event.timezone),
      end: event.end_at
        ? (event.all_day
          ? instantToDateInput(event.end_at, event.timezone)
          : instantToLocalInput(event.end_at, event.timezone))
        : '',
      timezone: event.timezone,
      location: event.location || '',
      reminder_lead_time: event.reminder_lead_time || event.reminder?.lead_time || ''
    });
    setFormError('');
    setShowForm(true);
  };

  const payloadFromForm = () => {
    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      event_type: form.event_type,
      timezone: form.timezone,
      location: form.location.trim() || null,
      reminder_lead_time: form.reminder_lead_time || null
    };
    if (form.all_day) {
      payload.start_date = form.start;
      if (form.end) payload.end_date = form.end;
    } else {
      payload.start_at = localDateTimeToIso(form.start, form.timezone);
      if (form.end) payload.end_at = localDateTimeToIso(form.end, form.timezone);
    }
    return payload;
  };

  const saveEvent = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFormError('');
    try {
      const payload = payloadFromForm();
      const response = editingId
        ? await calendarAPI.updateEvent(editingId, payload)
        : await calendarAPI.createEvent(payload);
      setEvents(current => editingId
        ? current.map(item => item.event_id === editingId ? response.data : item)
        : [...current, response.data]);
      closeForm();
    } catch (error) {
      setFormError(readableError(error));
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async (event) => {
    if (!window.confirm(`Delete “${event.title}” from your calendar?`)) return;
    try {
      await calendarAPI.deleteEvent(event.event_id);
      setEvents(current => current.filter(item => item.event_id !== event.event_id));
    } catch (error) {
      setLoadError(readableError(error));
    }
  };

  if (!user) {
    return (
      <main className="py-16 bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Calendar</h1>
          <p className="text-lg text-gray-600">Please sign in to use your calendar.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="py-12 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Your calendar</h1>
            <p className="text-gray-600 max-w-3xl">
              Organize dates that you enter or confirm. Good Parents Justice does not calculate legal deadlines,
              appeal periods, routes, or counting rules. Verify legal dates with an authoritative source.
            </p>
          </div>
          <button type="button" onClick={beginAdd} className="bg-blue-900 text-white px-5 py-3 rounded hover:bg-blue-800">
            Add calendar event
          </button>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-sm text-blue-900">
          Calendar reminders are organizational aids only. In local simulation, no real email is sent.
          Real email delivery is not configured in this release.
          {setCurrentView && (
            <button type="button" onClick={() => setCurrentView('appeals')} className="ml-2 underline font-medium">
              Review source-bound appeals timing information
            </button>
          )}
        </div>

        {loadError && (
          <div role="alert" className="bg-red-50 border border-red-200 text-red-800 rounded p-4 mb-6">
            {loadError}
            <button type="button" onClick={loadEvents} className="ml-2 underline">Try again</button>
          </div>
        )}

        <section aria-labelledby="calendar-events-heading" className="bg-white rounded-lg shadow p-6">
          <h2 id="calendar-events-heading" className="text-2xl font-bold text-gray-800 mb-5">Calendar events</h2>
          {loading ? (
            <p role="status" className="text-gray-600 py-8 text-center">Loading calendar events…</p>
          ) : sortedEvents.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-gray-600 mb-4">You have not added any calendar events.</p>
              <button type="button" onClick={beginAdd} className="bg-orange-500 text-white px-5 py-2 rounded hover:bg-orange-600">
                Add your first event
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedEvents.map(event => (
                <article key={event.event_id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{event.title}</h3>
                        <span className="text-xs bg-gray-100 text-gray-700 rounded px-2 py-1">{readableType(event.event_type)}</span>
                      </div>
                      <p className="text-sm text-gray-700">{formatEventDate(event)} ({event.timezone})</p>
                      {event.location && <p className="text-sm text-gray-600 mt-1">Location: {event.location}</p>}
                      {event.description && <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">{event.description}</p>}
                      {event.reminder && (
                        <p className="text-xs text-amber-800 bg-amber-50 rounded p-2 mt-3">
                          {reminderDescription(event.reminder)}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button type="button" onClick={() => beginEdit(event)} className="text-blue-800 border border-blue-200 px-3 py-2 rounded hover:bg-blue-50">
                        Edit
                      </button>
                      <button type="button" onClick={() => deleteEvent(event)} className="text-red-700 border border-red-200 px-3 py-2 rounded hover:bg-red-50">
                        Delete
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <section role="dialog" aria-modal="true" aria-labelledby="event-form-heading" className="bg-white rounded-lg p-6 w-full max-w-xl max-h-[92vh] overflow-y-auto">
            <h2 id="event-form-heading" className="text-2xl font-bold text-gray-800 mb-1">
              {editingId ? 'Edit calendar event' : 'Add calendar event'}
            </h2>
            <p className="text-sm text-gray-600 mb-5">Enter only a date you know or have confirmed.</p>
            <form onSubmit={saveEvent} className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                Title
                <input required maxLength="160" value={form.title} onChange={e => updateForm('title', e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded" />
              </label>

              <label className="block text-sm font-medium text-gray-700">
                Event type
                <select value={form.event_type} onChange={e => updateForm('event_type', e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded">
                  {EVENT_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>

              {form.event_type === 'user_confirmed_deadline' && (
                <div role="note" className="bg-yellow-50 border border-yellow-200 text-yellow-900 rounded p-3 text-sm">
                  Enter a deadline only after confirming it from the court, an official source, or a legal professional.
                  This calendar does not calculate it for you.
                </div>
              )}

              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input type="checkbox" checked={form.all_day} onChange={e => setForm(current => ({ ...current, all_day: e.target.checked, start: '', end: '' }))} />
                All-day event
              </label>

              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block text-sm font-medium text-gray-700">
                  Starts
                  <input required type={form.all_day ? 'date' : 'datetime-local'} value={form.start} onChange={e => updateForm('start', e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded" />
                </label>
                <label className="block text-sm font-medium text-gray-700">
                  Ends (optional)
                  <input type={form.all_day ? 'date' : 'datetime-local'} value={form.end} onChange={e => updateForm('end', e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded" />
                </label>
              </div>

              <label className="block text-sm font-medium text-gray-700">
                Timezone
                <select value={form.timezone} onChange={e => updateForm('timezone', e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded">
                  {TIMEZONES.map(value => <option key={value} value={value}>{value}</option>)}
                </select>
              </label>

              <label className="block text-sm font-medium text-gray-700">
                Reminder
                <select value={form.reminder_lead_time} onChange={e => updateForm('reminder_lead_time', e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded">
                  {REMINDER_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              {form.reminder_lead_time && (
                <p className="text-sm text-amber-800 bg-amber-50 rounded p-3">
                  No real email will be sent; local reminder processing is simulated.
                </p>
              )}

              <label className="block text-sm font-medium text-gray-700">
                Location (optional)
                <input maxLength="300" value={form.location} onChange={e => updateForm('location', e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded" />
              </label>

              <label className="block text-sm font-medium text-gray-700">
                Description (optional)
                <textarea maxLength="1000" rows="3" value={form.description} onChange={e => updateForm('description', e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded" />
              </label>

              {formError && <div role="alert" className="bg-red-50 text-red-800 rounded p-3">{formError}</div>}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={closeForm} className="border border-gray-300 px-4 py-2 rounded">Cancel</button>
                <button disabled={saving} type="submit" className="bg-blue-900 text-white px-4 py-2 rounded disabled:opacity-60">
                  {saving ? 'Saving…' : (editingId ? 'Save changes' : 'Add event')}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </main>
  );
};

export default Calendar;
